import React from 'react';
import { FileText, Upload, ShoppingCart, X, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PrescriptionHistory = ({
  isDoctor, loading,
  prescriptionHistory, fetchMedicineLinks,
  uploadedPrescriptions, setSelectedUploadedPrescription,
  setActiveTab, selectedUploadedPrescription, setSelectedUploadedPrescriptionState
}) => (
  <div className="space-y-6">
    {/* Doctor AI-Generated Prescriptions */}
    {isDoctor && (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2"><FileText className="w-5 h-5" />AI-Generated Prescriptions</h3>
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div><p className="mt-4 text-gray-600">Loading history...</p></div>
        ) : prescriptionHistory.length === 0 ? (
          <div className="text-center py-12"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-600">No AI-generated prescriptions yet</p></div>
        ) : (
          <div className="space-y-4" data-testid="doctor-prescription-history">
            {prescriptionHistory.map((prescription, index) => (
              <div key={`hist-${index}`} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">AI Generated</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${prescription.approved_by_doctor ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {prescription.approved_by_doctor ? 'Approved' : 'Pending'}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-800">{prescription.patient_info.patient_name}</h4>
                    <p className="text-sm text-gray-600">{prescription.patient_info.age} years, {prescription.patient_info.gender}</p>
                    <p className="text-sm text-blue-600 mt-1">Diagnosis: {prescription.diagnosis.diagnosis}</p>
                    <p className="text-xs text-gray-500 mt-2">{new Date(prescription.timestamp).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="text-sm text-gray-600">{prescription.suggested_medications?.length || 0} medications</p>
                    <Button onClick={() => fetchMedicineLinks(prescription.id, 'ai')} size="sm" className="bg-blue-600 hover:bg-blue-700"><ShoppingCart className="w-4 h-4 mr-1" />Order</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    )}

    {/* Patient Uploaded Prescriptions */}
    {!isDoctor && (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2"><Upload className="w-5 h-5" />My Uploaded Prescriptions</h3>
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div><p className="mt-4 text-gray-600">Loading uploads...</p></div>
        ) : uploadedPrescriptions.length === 0 ? (
          <div className="text-center py-12"><Upload className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-600">No uploaded prescriptions yet</p><Button onClick={() => setActiveTab('upload')} className="mt-4" variant="outline">Upload Your First Prescription</Button></div>
        ) : (
          <div className="space-y-4" data-testid="patient-upload-history">
            {uploadedPrescriptions.map((prescription, index) => (
              <div key={`up-${index}`} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Uploaded</span>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${prescription.extraction_status === 'completed' ? 'bg-green-100 text-green-700' : prescription.extraction_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {prescription.extraction_status}
                      </span>
                    </div>
                    <h4 className="font-bold text-gray-800">{prescription.patient_name}</h4>
                    <p className="text-sm text-gray-600">Medicines Extracted: {prescription.extracted_medicines?.length || 0}</p>
                    {prescription.notes && <p className="text-sm text-gray-500 mt-1 italic">Note: {prescription.notes}</p>}
                    <p className="text-xs text-gray-500 mt-2">Uploaded: {new Date(prescription.uploaded_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button onClick={() => setSelectedUploadedPrescription(prescription)} size="sm" variant="outline">View Details</Button>
                    {prescription.extraction_status === 'completed' && prescription.extracted_medicines?.some(m => !m.name.includes('manually') && !m.name.includes('failed') && !m.name.includes('No medicines')) && (
                      <Button onClick={() => fetchMedicineLinks(prescription.id, 'uploaded')} size="sm" className="bg-green-600 hover:bg-green-700"><ShoppingCart className="w-4 h-4 mr-1" />Order</Button>
                    )}
                  </div>
                </div>
                {prescription.extracted_medicines?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Extracted Medicines:</p>
                    <div className="flex flex-wrap gap-2">
                      {prescription.extracted_medicines.slice(0, 3).map((med, idx) => (
                        <span key={`em-${idx}`} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">{med.name.length > 30 ? med.name.substring(0, 30) + '...' : med.name}</span>
                      ))}
                      {prescription.extracted_medicines.length > 3 && <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">+{prescription.extracted_medicines.length - 3} more</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    )}

    {/* Patient Doctor Prescribed */}
    {!isDoctor && (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2"><FileText className="w-5 h-5" />Doctor Prescribed (AI Approved)</h3>
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div><p className="mt-4 text-gray-600">Loading prescriptions...</p></div>
        ) : prescriptionHistory.length === 0 ? (
          <div className="text-center py-12"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-600">No doctor-prescribed prescriptions yet</p></div>
        ) : (
          <div className="space-y-4">
            {prescriptionHistory.map((prescription, index) => (
              <div key={`doc-${index}`} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Doctor Prescribed</span>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">Approved</span>
                    </div>
                    <h4 className="font-bold text-gray-800">{prescription.patient_info?.patient_name || 'Patient'}</h4>
                    <p className="text-sm text-blue-600 mt-1">Diagnosis: {prescription.diagnosis?.diagnosis || 'N/A'}</p>
                    <p className="text-xs text-gray-500 mt-2">Date: {new Date(prescription.timestamp).toLocaleDateString()}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="text-sm text-gray-600">{prescription.suggested_medications?.length || 0} medications</p>
                    <Button onClick={() => fetchMedicineLinks(prescription.id, 'ai')} size="sm" className="bg-blue-600 hover:bg-blue-700"><ShoppingCart className="w-4 h-4 mr-1" />Order</Button>
                  </div>
                </div>
                {prescription.suggested_medications?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Prescribed Medicines:</p>
                    <div className="flex flex-wrap gap-2">
                      {prescription.suggested_medications.slice(0, 3).map((med, idx) => <span key={`pm-${idx}`} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">{med.medication_name}</span>)}
                      {prescription.suggested_medications.length > 3 && <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">+{prescription.suggested_medications.length - 3} more</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    )}
  </div>
);

export default PrescriptionHistory;
